import { fetchPhoto } from "./api.js";

/**
 * Put one photo on the clipboard, ready to paste into a chat.
 *
 * Stored photos are JPEG, but the async clipboard only takes `image/png` in
 * every browser that supports it at all, so the bytes are redrawn through a
 * canvas. The blob is handed over as a promise: Safari drops the click's
 * permission if anything is awaited before `write()` is called.
 *
 * Throws if the browser has no image clipboard or refuses the write; the caller
 * words the sentence.
 */
export async function copyPhotoToClipboard(id: string): Promise<void> {
  if (typeof ClipboardItem === "undefined" || !navigator.clipboard?.write) {
    throw new Error("This browser cannot copy images.");
  }
  const png = (async () => {
    const bytes = await fetchPhoto(id);
    const bitmap = await createImageBitmap(new Blob([bytes as BlobPart]));
    try {
      const canvas = document.createElement("canvas");
      canvas.width = bitmap.width;
      canvas.height = bitmap.height;
      canvas.getContext("2d")?.drawImage(bitmap, 0, 0);
      return await new Promise<Blob>((resolve, reject) =>
        canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("Encoding failed."))), "image/png"),
      );
    } finally {
      bitmap.close();
    }
  })();
  await navigator.clipboard.write([new ClipboardItem({ "image/png": png })]);
}
